import { __decorate } from "tslib";
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { chain, range, merge } from 'lodash';
import CharacterBuilderClassHitPoints from './CharacterBuilderClassHitPoints.vue';
import CharacterBuilderClassASI from './CharacterBuilderClassASI.vue';
import CharacterBuilderClassPowers from './CharacterBuilderClassPowers.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import MySelect from '@/components/MySelect.vue';
import ClassDetail from '@/components/ClassDetail.vue';
import CharactersArchetypeDetail from '@/pages/Characters/CharactersArchetypeDetail.vue';
import CharacterSheetExpansionFeatures from '../CharacterSheet/CharacterSheetExpansionFeatures.vue';
import MyDialog from '@/components/MyDialog.vue';
const archetypesModule = namespace('archetypes');
let CharacterBuilderClass = class CharacterBuilderClass extends Vue {
    constructor() {
        super(...arguments);
        this.range = range;
        this.isClassOpen = false;
        this.isArchetypeOpen = false;
    }
    created() {
        this.fetchArchetypes();
    }
    get classData() {
        const classData = this.classes.find(({ name }) => name === this.myClass.name);
        if (!classData)
            console.error('Class not found: ', this.myClass.name);
        return classData;
    }
    get archetypeData() {
        const myArchetype = this.myClass.archetype && this.myClass.archetype.name;
        const archetypeData = this.archetypes.find(({ name }) => name === myArchetype);
        if (myArchetype && !archetypeData)
            console.error('Archetype not found: ', myArchetype);
        return archetypeData;
    }
    get asiLevels() {
        return this.classData ? chain(this.classData.levelChanges)
            .map((value, level) => ({ ...value, level }))
            .filter(({ Features, level }) => Features.includes('Ability Score Improvement') && level <= this.myClass.levels)
            .map(({ level }) => parseInt(level))
            .value() : [];
    }
    get archetypeOptions() {
        return this.archetypes.filter(({ className }) => className === this.myClass.name).map(({ name }) => name);
    }
    get totalOtherClassesLevels() {
        return this.character.classes
            .filter(({ name }) => name !== this.myClass.name)
            .reduce((acc, { levels }) => acc + levels, 0);
    }
    get allForcePowers() {
        return [
            ...this.character.customForcePowers,
            ...this.character.classes.map(({ forcePowers }) => forcePowers || []).flat()
        ];
    }
    get classFeatures() {
        return chain(this.features)
            .filter(({ sourceName, level }) => this.classData
            ? this.classData.name === sourceName && level <= this.myClass.levels
            : false)
            .sortBy('level')
            .reverse()
            .uniqBy(({ name, sourceName }) => name + sourceName)
            .reverse()
            .value();
    }
    get archetypeFeatures() {
        return chain(this.features)
            .filter(({ sourceName, level }) => this.archetypeData
            ? this.archetypeData.name === sourceName && level <= this.myClass.levels
            : false)
            .sortBy('level')
            .reverse()
            .uniqBy(({ name, sourceName }) => name + sourceName)
            .reverse()
            .value();
    }
    handleUpdateLevels(levels) {
        const amount = this.classData ? this.classData.hitPointsAtHigherLevelsNumber : 0;
        const newFixedHP = Array(this.index === 0 ? levels - 1 : levels).fill(amount);
        const hitPoints = merge([], newFixedHP, this.myClass.hitPoints).slice(0, newFixedHP.length);
        const archetype = levels < 3 ? undefined : this.myClass.archetype;
        const newLevel = this.totalOtherClassesLevels + levels;
        const newLevelAdvancement = this.characterAdvancements.find(({ level }) => level === newLevel);
        const experiencePoints = Math.max(newLevelAdvancement ? newLevelAdvancement.experiencePoints : 0, this.character.experiencePoints);
        const abilityScoreImprovements = this.myClass.abilityScoreImprovements.slice(0, this.asiLevels.length);
        this.$emit('replaceCharacterProperties', [
            {
                path: 'classes.' + this.index,
                property: {
                    ...this.myClass,
                    levels,
                    hitPoints,
                    abilityScoreImprovements,
                    archetype
                }
            },
            { path: 'experiencePoints', property: experiencePoints }
        ]);
    }
    handleUpdateArchetype(name) {
        this.$emit('replaceCharacterProperty', {
            path: `classes.${this.index}.archetype`,
            property: { name }
        });
    }
    handleUpdateASI(index, newASI) {
        this.$emit('replaceCharacterProperty', {
            path: `classes.${this.index}.abilityScoreImprovements.${index}`,
            property: newASI
        });
    }
    handleUpdatePowers(newPowers, type, isArchetype) {
        this.$emit('replaceCharacterProperty', {
            path: `classes.${this.index}.${isArchetype ? 'archetype.' : ''}${type.toLowerCase()}Powers`,
            property: newPowers
        });
    }
    handleUpdateHitPoints(newValue, hpIndex) {
        this.$emit('updateCharacter', { classes: { [this.index]: { hitPoints: { [hpIndex]: newValue } } } });
    }
    handleDeleteClass() {
        const advancement = this.characterAdvancements.find(({ level }) => level === this.totalOtherClassesLevels);
        const experiencePoints = advancement ? advancement.experiencePoints : 0;
        let newClasses = this.character.classes.filter((_, index) => index !== this.index);
        // Starting class changed, so use its max hit points for level 1
        if (this.index === 0 && newClasses.length)
            newClasses[0].hitPoints = newClasses[0].hitPoints.slice(1);
        this.$emit('replaceCharacterProperties', [
            {
                path: 'classes',
                property: newClasses
            },
            { path: 'experiencePoints', property: experiencePoints }
        ]);
    }
};
__decorate([
    Prop(Object)
], CharacterBuilderClass.prototype, "character", void 0);
__decorate([
    Prop(Array)
], CharacterBuilderClass.prototype, "classes", void 0);
__decorate([
    Prop(Array)
], CharacterBuilderClass.prototype, "features", void 0);
__decorate([
    Prop(Object)
], CharacterBuilderClass.prototype, "myClass", void 0);
__decorate([
    Prop(Number)
], CharacterBuilderClass.prototype, "index", void 0);
__decorate([
    Prop(Boolean)
], CharacterBuilderClass.prototype, "isFixedHitPoints", void 0);
__decorate([
    Prop(Array)
], CharacterBuilderClass.prototype, "characterAdvancements", void 0);
__decorate([
    archetypesModule.State
], CharacterBuilderClass.prototype, "archetypes", void 0);
__decorate([
    archetypesModule.Action
], CharacterBuilderClass.prototype, "fetchArchetypes", void 0);
CharacterBuilderClass = __decorate([
    Component({
        components: {
            CharacterBuilderClassHitPoints,
            CharacterBuilderClassASI,
            CharacterBuilderClassPowers,
            CharacterSheetExpansionFeatures,
            ConfirmDelete,
            ClassDetail,
            CharactersArchetypeDetail,
            MyDialog,
            MySelect
        }
    })
], CharacterBuilderClass);
export default CharacterBuilderClass;
